/**
 * Does this look like a real person's name?
 *
 * ⚠️ MIRROR of fex-platform-service/src/utils/person-name.util.ts. Separate
 * repos, so it cannot be imported — the two copies MUST be changed together or
 * the form and the API will disagree about what a valid name is. The server
 * copy is the authoritative one; this exists so people are told at the field
 * instead of at submit.
 *
 * WHY: 2026-08-23 a fraudulent signup came through as name "34534534" with a
 * matching junk phone. Nothing validated the field, so keyboard mash and digit
 * strings sailed into the CRM and onto agent-facing screens.
 *
 * ⚠️ DELIBERATELY PERMISSIVE. This runs on real people at signup, and a name
 * validator that is too clever locks out legitimate customers — which is far
 * worse than letting one bad name through, since we can edit a name later but
 * cannot recover a lost signup. Every rule below rejects only things that
 * cannot be a name, never things that merely look unusual.
 *
 * Explicitly accepted: accents and non-Latin scripts (José, Renée, Müller,
 * 李伟), apostrophes (O'Brien, D'Angelo), hyphens (Mary-Jane, Sinclair-Voigt),
 * particles (Juan Carlos de la Cruz), initials (J. Smith), suffixes (Jr., III).
 */

/** Straight and curly apostrophes, hyphen variants, periods — all legal in names. */
const NAME_PUNCT = /[’'`´\-‐‑–—.]/u;

/** Collapse runs of whitespace so "John   Smith" is two parts, not four. */
export function normalizePersonName(raw?: string | null): string {
  return String(raw ?? '').replace(/\s+/gu, ' ').trim();
}

export interface NameCheck {
  valid: boolean;
  reason?: string;
}

export function checkPersonName(raw?: string | null): NameCheck {
  const name = normalizePersonName(raw);

  // Say WHAT is expected, every time. "Please enter your full name" reads as
  // already satisfied by "John", which is the exact confusion this validator
  // exists to remove.
  if (!name) return { valid: false, reason: 'Please enter your first and last name.' };
  if (name.length < 3) return { valid: false, reason: 'Please enter your first and last name.' };
  if (name.length > 100) return { valid: false, reason: 'That name is too long.' };

  // A digit anywhere is disqualifying. Real names do not contain them, and this
  // is what catches "34534534" and "Agent 11".
  if (/\d/u.test(name)) {
    return { valid: false, reason: 'Please enter your name as it appears on your license — letters only.' };
  }

  // Anything that is not a letter, mark, space or name punctuation.
  if (/[^\p{L}\p{M}\s’'`´\-‐‑–—.]/u.test(name)) {
    return { valid: false, reason: 'Please enter a valid name.' };
  }

  const parts = name.split(' ').filter(Boolean);
  if (parts.length < 2) {
    return { valid: false, reason: 'Please enter your first and last name.' };
  }

  // Every part needs a letter — blocks "John ---" and stray punctuation parts.
  for (const part of parts) {
    if (!/\p{L}/u.test(part)) {
      return { valid: false, reason: 'Please enter your first and last name.' };
    }
  }

  // At least one part must be a real word. One initial is normal ("J. Smith");
  // a name made entirely of initials is not something we can use.
  //
  // ⚠️ "Real word" CANNOT mean "2+ letters". CJK names are one character per
  // part — 李 伟 is a complete, ordinary name — and an ideograph carries a whole
  // word. Requiring two letters silently rejected every Chinese, Japanese and
  // Korean name (caught in testing, not in prod).
  const isWordish = (part: string) =>
    (part.match(/\p{L}/gu) || []).length >= 2 ||
    /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u.test(part);
  if (!parts.some(isWordish)) {
    return { valid: false, reason: 'Please enter your first and last name.' };
  }

  // Keyboard mash: every part is one character typed repeatedly ("aaaa bbbb").
  // Checked PER PART — a whole-string check misses it the moment the mash uses
  // two different keys. Real names never look like this.
  const isMash = (part: string) => {
    const l = (part.match(/\p{L}/gu) || []).join('').toLowerCase();
    return l.length >= 3 && new Set(l).size === 1;
  };
  if (parts.every(isMash)) {
    return { valid: false, reason: 'Please enter a valid name.' };
  }

  return { valid: true };
}

export function isValidPersonName(raw?: string | null): boolean {
  return checkPersonName(raw).valid;
}
